import { apiGet } from "../apiUtils";

/**
 * Mirrors the Event type from @aws-sdk/client-bedrock-agentcore.
 * The backend serializes SDK Event objects directly with no transformation.
 */
export interface ConversationEvent {
  memoryId?: string;
  actorId?: string;
  sessionId?: string;
  eventId?: string;
  eventTimestamp?: string | Date;
  payload?: Record<string, unknown>[];
}

/**
 * Conversation history data from GET /projects/{projectId}/conversation
 */
export interface ConversationHistoryData {
  events: ConversationEvent[];
  nextToken?: string;
}

/**
 * API response wrapper for conversation history
 */
export interface ConversationHistoryResponse {
  success: boolean;
  data: ConversationHistoryData;
  requestId?: string;
}

/**
 * Generates a consistent conversation ID for API-loaded conversations.
 * Used to identify and deduplicate conversations loaded from the API.
 */
export const getApiConversationId = (projectId: string) => `conv_${projectId}`;

/**
 * Parameters for requesting conversation history
 */
export interface GetConversationHistoryParams {
  projectId: string;
  nextToken?: string;
}

/**
 * Retrieves conversation history for a specific project
 *
 * @param params - Parameters including projectId and optional nextToken for pagination
 * @returns Promise resolving to conversation history with events and pagination token
 * @throws Error if the API request fails or returns invalid data
 *
 * @example
 * ```typescript
 * const history = await getConversationHistory({
 *   projectId: "project-123"
 * });
 * console.log(history.data.events);
 *
 * // With pagination
 * const nextPage = await getConversationHistory({
 *   projectId: "project-123",
 *   nextToken: history.data.nextToken
 * });
 * ```
 */
export const getConversationHistory = async (
  params: GetConversationHistoryParams,
): Promise<ConversationHistoryResponse> => {
  const queryParams: Record<string, string> = {};

  if (params.nextToken) {
    queryParams.NextToken = params.nextToken;
  }

  const restOperation = apiGet(`/projects/${params.projectId}/conversation`, {
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });
  const { body } = await (await restOperation).response;
  return (await body.json()) as unknown as ConversationHistoryResponse;
};
